import GuideLayout from '../../layouts/GuideLayout'
import type { TocItem } from '../../layouts/GuideLayout'
import { AffiliateCTA } from '../../components/content/AffiliateCTA'
import { CalculatorEmbed } from '../../components/content/CalculatorEmbed'

const tocItems: TocItem[] = [
  { id: 'base-vs-transactions',  label: 'How Azure disk pricing works: base fee vs transactions', level: 2 },
  { id: 'standard-hdd',         label: 'Standard HDD: the no-cap trap',                         level: 2 },
  { id: 'standard-ssd',         label: 'Standard SSD: break-even vs HDD',                       level: 2 },
  { id: 'premium-ssd',          label: 'Premium SSD: no transactions, predictable cost',         level: 2 },
  { id: 'premium-v2-ultra',     label: 'Premium SSD v2 and Ultra: 3-dimensional pricing',        level: 2 },
  { id: 'right-sizing',         label: 'Disk sizing guide: avoiding over-provisioning',          level: 2 },
  { id: 'calculator',           label: 'Managed Disks Calculator',                              level: 2 },
]

const bottomCta = (
  <AffiliateCTA
    programme="Pluralsight"
    courseName="AZ-900 Microsoft Azure Fundamentals"
    url="https://www.pluralsight.com/paths/az-900-microsoft-azure-fundamentals"
    price="From £29/month"
    badgeText="Azure Storage · Managed Disks · VM sizing"
  />
)

export default function AzureManagedDisksPricingGuide() {
  return (
    <GuideLayout
      title="Azure Managed Disks Pricing Guide UK 2026"
      description="Azure Standard HDD has no transaction cap. That single fact costs teams money every month when they choose HDD over SSD because the per-GB price is lower. For a workload generating 10 million I/O operations per month — typical for a busy database — HDD transaction charges add £46/month per disk on top of the base fee. The equivalent Standard SSD disk costs less in total the moment your workload becomes even moderately active. The only workloads where HDD wins on total cost are truly cold storage scenarios with near-zero IOPS."
      lastUpdated="April 2026"
      canonical="https://www.azure-calc.co.uk/guides/azure-managed-disks-pricing-uk"
      metaTitle="Azure Managed Disks Pricing Guide UK 2026 | HDD vs SSD Cost Comparison | AzureCalc.uk"
      metaDescription="Understand Azure Managed Disks pricing in GBP for UK developers. Covers Standard HDD no-cap transaction trap, Standard SSD break-even, Premium SSD fixed pricing, and Premium SSD v2 3D billing."
      tocItems={tocItems}
      relatedCalculators={[
        { service: 'managed-disks', path: '/managed-disks', label: 'Managed Disks Calculator' },
        { service: 'virtual-machines', path: '/virtual-machines', label: 'Virtual Machines Calculator' },
      ]}
      bottomCta={bottomCta}
    >
      <h2 id="base-vs-transactions">How Azure disk pricing works: base fee vs transaction charges</h2>
      <p>
        Azure Managed Disks have two billing components: a fixed monthly fee per SKU (based on the disk size
        you reserve) and, for Standard tiers, a transaction charge per 10,000 I/O operations. The fixed fee
        applies regardless of how much you use the disk — you pay for the reserved capacity. The transaction
        charge is consumption-based: every read and write counts.
      </p>
      <p>
        The split matters because it means two disks of the same size can have very different monthly bills
        depending on how actively they are used. A 128 GB Standard HDD sitting cold costs £4.87/month.
        The same disk serving a busy workload at 10 million I/O operations per month costs £4.87 + £46 = £50.87.
        The disk that looked cheapest on the pricing page is now the most expensive option.
      </p>
      <p>
        Premium SSD removes this uncertainty entirely. It has no transaction billing at all — the monthly fee
        is fixed regardless of IOPS. That predictability is worth paying for in production environments where
        traffic is variable or hard to estimate.
      </p>

      <h2 id="standard-hdd">Standard HDD: the no-cap trap, when it makes sense</h2>
      <p>
        Standard HDD (S-series) is the lowest base cost disk tier: an S10 (128 GB) costs £4.87/month.
        The transaction rate is £0.0005 per 10,000 I/O operations with no monthly cap. There is no ceiling —
        the bill accumulates for every operation regardless of how many you generate.
      </p>
      <p>
        The trap: most engineers compare the per-GB storage rate and choose HDD because it looks cheaper.
        They do not model the transaction component because it requires knowing their actual IOPS in advance.
        For a disk that sees even moderate workload — a development database, a log volume on a busy application
        server, a VM with OS writes — the transaction charges quickly exceed the saving on the base fee.
      </p>
      <p>
        Standard HDD is genuinely the right choice for exactly one scenario: cold archival or backup storage
        with near-zero IOPS. If the disk will be written to once and rarely read, the transaction charges
        remain negligible and HDD wins on base fee. If anything will actively read or write to the disk
        during normal operation, model the transaction cost before committing to HDD.
      </p>

      <h2 id="standard-ssd">Standard SSD: the transaction rate and break-even vs HDD</h2>
      <p>
        Standard SSD (E-series) charges £0.0015 per 10,000 I/O operations for LRS. The transaction rate
        is higher per operation than HDD (£0.0015 vs £0.0005), but the base disk fee is also higher.
        The practical comparison for most workloads is not Standard HDD vs Standard SSD — it is Standard
        HDD vs Premium SSD, because Premium SSD eliminates the transaction variable entirely.
      </p>
      <p>
        Standard SSD sits in the middle: more predictable than HDD for variable workloads, cheaper base
        cost than Premium SSD. It is a reasonable choice for non-critical workloads that have moderate
        and fairly predictable IOPS — development environments, test databases, infrequently-accessed
        application volumes. For any production workload where unpredictable IOPS spikes could cause
        unexpected bills, Premium SSD is the safer default.
      </p>
      <p>
        Use the <a href="/managed-disks">calculator</a> to see the break-even transaction volume for
        your specific disk size — the point at which HDD total cost (base + transactions) equals SSD
        base cost alone.
      </p>

      <h2 id="premium-ssd">Premium SSD: why no transactions makes it predictable for production</h2>
      <p>
        Premium SSD (P-series) has no transaction billing. The monthly fee is fixed regardless of how
        many I/O operations you perform. A P10 (128 GB) costs £17.94/month whether you generate zero
        IOPS or run it at maximum throughput. This predictability is the primary reason Premium SSD
        is the default recommendation for production workloads.
      </p>
      <p>
        The P-series base fee is 2–3× higher than the equivalent E-series SKU: P10 at £17.94 vs
        E10 at £7.95. For a truly idle disk, E10 is cheaper. For any disk that sees production traffic,
        the transaction charges on E10 typically close that gap within the first week of a busy month.
        At 10 million I/O operations per month, E10 adds £15 in transaction charges — making its
        total cost (£22.95) higher than P10 (£17.94).
      </p>
      <p>
        Premium SSD also provides better IOPS and throughput guarantees per SKU than Standard tiers.
        The combination of predictable billing and guaranteed performance makes it appropriate for
        SQL Server data files, production web servers, and any workload where latency consistency matters.
      </p>

      <h2 id="premium-v2-ultra">Premium SSD v2 and Ultra: 3-dimensional pricing explained</h2>
      <p>
        Premium SSD v2 and Ultra Disk use a different billing model from the S/E/P-series tiers.
        Instead of a fixed SKU price, they bill across three independent dimensions:
      </p>
      <ul>
        <li><strong>Capacity:</strong> charged per GiB per month (£0.073/GiB for Pv2, £0.146/GiB for Ultra)</li>
        <li><strong>IOPS:</strong> charged per provisioned IOPS per month — beyond a baseline for Pv2 (first 3,000 IOPS free)</li>
        <li><strong>Throughput:</strong> charged per provisioned MB/s per month — beyond a baseline for Pv2 (first 125 MB/s free)</li>
      </ul>
      <p>
        This means the disk size, IOPS, and throughput can be independently scaled — unlike P-series where
        the IOPS ceiling is bundled with the SKU. A 128 GiB Pv2 disk with 10,000 IOPS costs more than the
        same disk with 4,000 IOPS. A P10 (128 GB) always includes the same IOPS ceiling regardless of how
        much you actually need.
      </p>
      <p>
        Premium SSD v2 is suited to workloads that need fine-grained performance control — databases that
        require specific IOPS tiers not aligned with the P-series SKU boundaries. Ultra Disk is for the
        highest performance requirements (sub-millisecond latency, very high IOPS) and is only available
        in specific VM sizes and availability zones.
      </p>
      <p>
        Note: the Azure Retail Prices API returns £0 for the IOPS and throughput meters on both Pv2 and
        Ultra Disk. The rates used in this calculator are hardcoded from Microsoft documentation and
        verified in April 2026. Check the <a href="/methodology">Methodology page</a> for the exact
        source and verification date.
      </p>

      <h2 id="right-sizing">Disk sizing guide: avoiding over-provisioning</h2>
      <p>
        The most common disk sizing mistake is choosing the next SKU up &ldquo;just in case.&rdquo;
        Each P-series tier roughly doubles in size and cost — P10 (128 GB, £17.94) to P15 (256 GB, £34.61).
        If your workload uses 130 GB, you must take P15, but if it uses 100 GB you can stay on P10.
        Measure actual disk usage before sizing, not allocated partitions.
      </p>
      <p>
        For Standard HDD and SSD, over-provisioning is less costly in absolute terms but the transaction
        charges are unaffected by disk size — a larger SKU does not reduce your per-operation billing.
        The only reason to size up on Standard tiers is storage capacity, not performance, since
        performance is determined by VM type and I/O scheduling rather than disk SKU.
      </p>
      <p>
        For Premium SSD v2, right-sizing matters differently: you provision capacity, IOPS, and throughput
        independently. Start at the minimum you need for each dimension and scale them separately as your
        workload profile becomes clear from monitoring.
      </p>

      <h2 id="calculator">Managed Disks Calculator</h2>
      <p>
        Use the <a href="/managed-disks">Managed Disks Calculator →</a> to estimate your costs.
        The HDD vs SSD comparison panel shows the break-even transaction volume — the point
        at which HDD total cost equals SSD base cost. Enter your estimated monthly I/O operations
        to see whether HDD is genuinely cheaper for your workload.
      </p>

      <p className="text-[12px] italic" style={{ color: 'var(--text-tertiary)' }}>
        Built and verified by an independent Azure engineer.
      </p>

      <CalculatorEmbed service="managed-disks" />
    </GuideLayout>
  )
}
